import React, { useState, useRef, useEffect, Fragment, useCallback } from "react";
import { MenuItem, InputBase, Popover, Checkbox, CircularProgress, Box } from "@mui/material";
import { FiChevronDown } from "react-icons/fi";

export default function SelectDropDownWithCheckBox({
  label = "",
  options = [],
  onClickHandler,
  selectedOption,
  title,
  selectedOptions = [], // Prop to get the selected options from the parent component
  allOptionSelected = false,
  showAllOption = true,
  disabled = false,
  className = "",
  menuClassName = "",
  // Infinite scroll props
  onScrollToBottom = null,
  isLoadingMore = false,
  hasMore = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null); // State to control dropdown
  const [anchorElWidth, setAnchorElWidth] = useState(null);

  const [searchText, setSearchText] = useState(""); // State for search input
  const inputRef = useRef(null); // Reference for focusing input
  const containerRef = useRef(null);
  const isDropdownOpen = Boolean(anchorEl);
  // Open dropdown menu
  const handleMenuOpen = (event) => {
    if (disabled) return;
    const anchor = containerRef.current || event.currentTarget;
    setAnchorEl(anchor);
    setAnchorElWidth(anchor.clientWidth);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  // Close dropdown menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSearchText(""); // Clear the search text when closing the menu
  };

  // Handle search text change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    // Check if scrolled to bottom (with 50px threshold)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (onScrollToBottom && hasMore && !isLoadingMore) {
        onScrollToBottom();
      }
    }
  }, [onScrollToBottom, hasMore, isLoadingMore]);

  // Filter options based on search input
  const filteredOptions = options?.filter((option) =>
    option[title]?.toLowerCase().includes(searchText.toLowerCase())
  );
  const rotateClass = (open) => (open ? "-rotate-180" : "rotate-0");
  return (
    <div
      ref={containerRef}
      className={`${className} qv-multi-select border border-[#E3E3E3] rounded ${
        disabled
          ? "bg-[#f2f2f2] cursor-not-allowed"
          : "cursor-pointer"
      } `}
    >
      {/* Trigger dropdown */}
      <div
        onClick={handleMenuOpen}
        className="flex items-center justify-between cursor-pointer p-2"
      >
        <p
          className={
            selectedOptions.length > 0 ? "text-[]" : "text-[#000000de]"
          }
        >
          {selectedOptions.length > 0 ? selectedOptions.join(", ") : label}
        </p>
        <div className="">
          <FiChevronDown
            size={20}
            className={`transition-transform duration-500 ${rotateClass(
              Boolean(anchorEl)
            )}`}
            // src={upArrow}
          />
        </div>
      </div>

      {/* Dropdown menu */}
      <Popover
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleMenuClose}
        PaperProps={{
          className: `custom-scroll no-top-shadow qv-multi-select__menu${menuClassName ? ` ${menuClassName}` : ""}`,
          style: {
            width: anchorElWidth,
            maxHeight: 300,
            marginTop: 6,
          },
          onScroll: handleScroll,
        }}
        anchorOrigin={{
          vertical: "bottom",
        }}
        MenuListProps={{
          style: { paddingTop: 10 },
        }}
      >
        {/* Search input */}
        <MenuItem
          disableTouchRipple
          sx={{
            "&.Mui-focusVisible": {
              backgroundColor: "var(--qv-bg-ffffff, #fff) !important", // Remove background color when focused
            },
            "&:hover": {
              backgroundColor: "var(--qv-bg-ffffff, #fff)", // Remove or customize hover background color
            },
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9,
            background: "var(--qv-bg-ffffff, #fff)",
            px: 1,
          }}
        >
          <InputBase
            sx={{
              background: "var(--qv-bg-ffffff, #fff)",
              "&.Mui-focused": {
                background: "var(--qv-bg-ffffff, #fff)", // Remove background color when focused
              },
              "&.Mui-focusVisible": {
                backgroundColor: "var(--qv-bg-ffffff, #fff) !important", // Remove background color when focused
              },
              "&:hover": {
                backgroundColor: "var(--qv-bg-ffffff, #fff) !important", // Remove or change the hover background color
              },
            }}
            placeholder="Search..."
            onChange={handleSearchChange}
            value={searchText}
            // inputRef={inputRef}
            fullWidth
          />
        </MenuItem>
        {showAllOption && options?.length > 1 && (
          <MenuItem sx={{ p: 0 }} onClick={() => onClickHandler("All")}>
            <Checkbox checked={allOptionSelected} />
            All
          </MenuItem>
        )}

        {/* Render filtered options */}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <Fragment key={index}>
              <MenuItem
                sx={{ p: 0 }}
                key={index}
                // Per-option lock (e.g. Unpaid while "All" is active on the
                // in-store Payment Type filter). Options without the flag
                // behave exactly as before.
                disabled={!!option.disabled}
                onClick={() => onClickHandler(option)}
              >
                <Checkbox
                  checked={selectedOption?.some((item) =>
                    item.id
                      ? item.id === option.id
                      : item[title] === option[title]
                  )}
                />
                <span className={`CircularSTDBook-15px ${option?.className}`}>
                  {option[title]}
                </span>
              </MenuItem>
            </Fragment>
          ))
        ) : (
          <MenuItem disabled>No options found</MenuItem>
        )}

        {/* Loading indicator for infinite scroll */}
        {isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Show load more hint if there's more data */}
        {hasMore && !isLoadingMore && filteredOptions.length > 0 && (
          <Box sx={{ textAlign: 'center', py: 1, color: 'var(--qv-tx-999999, #999)', fontSize: '12px' }}>
            Scroll for more...
          </Box>
        )}
      </Popover>
    </div>
  );
}
